import { ChangeEvent, forwardRef, useContext, useRef } from "react";
import css from "@styled-system/css";
import styled, { ThemeContext } from "styled-components";

import Typography from "../Typography/Typography";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

import { CheckboxProps } from "./checkbox.type";

const Label = styled.label<{ css?: string }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  position: relative;
  ${(props) => props.css};
`;

const Input = styled.input`
  cursor: pointer;
  opacity: 0;
  width: 24px;
  height: 24px;
  position: absolute;
  margin: 0;

  &:focus + svg {
    outline: 2px solid ${(props) => props.theme.colors["outline-color"]};
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) + svg {
    outline: none;
  }

  ${css({
    backgroundColor: "checkbox-bg-color",
    "&:disabled + svg": {
      opacity: 0.5,
    },
  })}

  &.error + svg {
    ${css({
      border: "errorBorder",
    })};
  }
`;

const ErrorMessageContainer = styled.div`
  p:first-of-type {
    margin-top: 0;
    margin-left: 40px;
  }
`;

const getSVG = (checked: boolean, error: boolean, id: string, fill: string) => {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      style={{
        borderRadius: "4px",
        marginRight: "12px",
        cursor: "pointer",
      }}
    >
      <rect
        className="checkbox-background"
        width="24"
        height="24"
        x="0"
        y="0"
        stroke="#efefef"
        fill={checked ? fill : "white"}
        strokeWidth={checked || error ? "0" : "4"}
        rx="4"
        ry="4"
      ></rect>
      <path
        x="5"
        y="0"
        width="20"
        height="10"
        fill="white"
        transform="scale(0.031)
            translate(139 150)"
        d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
      ></path>
    </svg>
  );
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      checked,
      label,
      onChange,
      disabled = false,
      required = false,
      error = false,
      errorMessage,
      children,
      ...rest
    }: CheckboxProps,
    ref
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const requiredSymbol =
      label && required
        ? `&:after {
          content: "*";
          color: red;
        }`
        : "";
    const { colors } = useContext(ThemeContext);

    return (
      <>
        <Label htmlFor={id} {...rest}>
          <Input
            type="checkbox"
            id={id}
            checked={checked}
            disabled={disabled}
            required={required}
            ref={ref || internalRef}
            className={error ? "error" : ""}
            onChange={(evt: ChangeEvent<HTMLInputElement>) => {
              onChange(evt.target.checked, id, evt);
            }}
          />
          {getSVG(checked, error, id, colors["checkbox-fill-color"])}
          {!children && (
            <Typography
              variant="body"
              htmlTag="span"
              color="checkbox-font-color"
              css={`
                ${requiredSymbol}
                flex: 1;
              `}
            >
              {label}
            </Typography>
          )}
          {children && children}
        </Label>
        {error && errorMessage && (
          <ErrorMessageContainer>
            <ErrorMessage errorMessage={errorMessage} />
          </ErrorMessageContainer>
        )}
      </>
    );
  }
);

export default Checkbox;
